import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { decimals } from '../../../../helpers/jsHelper';
import { convertCMToActiveUnits } from '../../../../helpers/curatorActions';
import {
  getLocationFromData,
  MIN_ROWS_COLUMNS,
  MIN_SCALE,
  POSITION_DATA,
  VIEW,
} from './commonHelper';
import { toUnityFixed } from '../../../../helpers/unityHelper';

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 12,
  },
  table: {
    marginTop: 10,
    width: '100%',
    borderLeft: '1px solid black',
    borderTop: '1px solid black',
  },
  row: {
    flexDirection: 'row',
    borderBottom: '1px solid black',
  },
  cell: {
    flex: 1,
    padding: 5,
    borderRight: '1px solid black',
  },
  image: {
    width: '100%',
    height: 200,
    marginBottom: 10,
    objectFit: 'cover',
  },
  multiLineCell: { flex: 1, padding: 5, borderRight: '1px solid black', flexDirection: 'column' },
});

// PDF Component
export const MattressPdf = ({ imageUrl, data }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Image */}
        {imageUrl && <Image src={imageUrl} style={styles.image} />}
        {/* Table */}
        <View style={styles.table}>
          {/* Table Rows */}
          {data?.size && (
            <View style={styles.row}>
              <Text style={styles.cell}>Size</Text>
              <Text style={styles.cell}>{data.size}</Text>
            </View>
          )}
          {data?.height && (
            <View style={styles.row}>
              <Text style={styles.cell}>Height</Text>
              <Text style={styles.cell}>{data.height}</Text>
            </View>
          )}
          {data?.topData?.map((topData, index) => {
            return (
              <div key={index}>
                {topData?.topName && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Top: Variant</Text>
                    <Text style={styles.cell}>{topData?.topName}</Text>
                  </View>
                )}
                {topData?.patternId && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Top: Quilting Pattern</Text>
                    <View style={styles.multiLineCell}>
                      <Text>Name: {topData.patternName}</Text>
                      <Text>
                        Width ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(topData.width), 3)}
                      </Text>
                      <Text>
                        Height ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(topData.height), 3)}
                      </Text>
                      <Text>PositionX: {decimals(topData.positionX, 3)}</Text>
                      <Text>PositionY: {decimals(topData.positionY, 3)}</Text>
                      <Text>Rotation: {decimals(topData.rotation, 3)}</Text>
                      <Text>Depth: {decimals(topData.displacementIntensity, 3)}</Text>
                    </View>
                  </View>
                )}
                {topData?.gussetPatternId && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Top: Gusset</Text>
                    <View style={styles.multiLineCell}>
                      <Text>Name: {topData.gussetPatternName}</Text>
                      <Text>
                        Width ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(topData.widthGusset), 3)}
                      </Text>
                      <Text>
                        Height ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(topData.heightGusset), 3)}
                      </Text>
                      <Text>PositionX: {decimals(topData.positionXGusset, 3)}</Text>
                      <Text>PositionY: {decimals(topData.positionYGusset, 3)}</Text>
                      <Text>Rotation: {decimals(topData.rotationGusset, 3)}</Text>
                      <Text>Depth: {decimals(topData.displacementIntensityGusset, 3)}</Text>
                    </View>
                  </View>
                )}
              </div>
            );
          })}
          {data?.topButtons?.map((button, index) => {
            return (
              <View style={styles.row} key={index}>
                <Text style={styles.cell}>
                  Top: Tuft{data?.topButtons.length > 1 && index > 0 && `-${index + 1}`}
                </Text>
                <View style={styles.multiLineCell}>
                  <Text>Variant: {button?.name || '-'}</Text>
                  <Text>Starts From X: {decimals(button?.startsFromPosX || 0, 3)}</Text>
                  <Text>Starts From Y: {decimals(button?.startsFromPosY || 0, 3)}</Text>
                  <Text>
                    Columns Count: {decimals(button?.columnsCount || MIN_ROWS_COLUMNS, 3)}
                  </Text>
                  <Text>Columns Gap: {decimals(button?.columnsGap || 0, 3)}</Text>
                  <Text>Rows Count: {decimals(button?.rowsCount || MIN_ROWS_COLUMNS, 3)}</Text>
                  <Text>Rows Gap: {decimals(button?.rowsGap || 0, 3)}</Text>
                  <Text>
                    Offset Alternate Rows: {decimals(button?.offsetAlternateRows || 0, 3)}
                  </Text>
                  <Text>Diameter Size: {decimals(button?.tuftedButtonScale || MIN_SCALE, 3)}</Text>
                </View>
              </View>
            );
          })}
          {data?.wall?.wallName && (
            <View style={styles.row}>
              <Text style={styles.cell}>Wall: Variant</Text>
              <Text style={styles.cell}>{data.wall.wallName}</Text>
            </View>
          )}
          {data?.wall?.patternId && (
            <View style={styles.row}>
              <Text style={styles.cell}>Wall: Quilting Pattern</Text>
              <View style={styles.multiLineCell}>
                <Text>Name: {data?.wall?.patternName}</Text>
                <Text>
                  Width ({data.measureUnits}):{' '}
                  {decimals(convertCMToActiveUnits(data?.wall?.width), 3)}
                </Text>
                <Text>
                  Height ({data.measureUnits}):{' '}
                  {decimals(convertCMToActiveUnits(data?.wall?.height), 3)}
                </Text>
                <Text>PositionX: {decimals(data?.wall?.positionX, 3)}</Text>
                <Text>PositionY: {decimals(data?.wall?.positionY, 3)}</Text>
                <Text>Rotation: {decimals(data?.wall?.rotation, 3)}</Text>
                <Text>Depth: {decimals(data?.wall?.displacementIntensity, 3)}</Text>
              </View>
            </View>
          )}
          {data?.wallButtons?.map((button, index) => {
            return (
              <View style={styles.row} key={index}>
                <Text style={styles.cell}>
                  Wall: Tuft{data?.topButtons.length > 1 && index > 0 && `-${index + 1}`}
                </Text>
                <View style={styles.multiLineCell}>
                  <Text>Variant: {button?.name || '-'}</Text>
                  <Text>Location: {getLocationFromData(button?.sideLocation)}</Text>
                  <Text>Starts From X: {decimals(button?.startsFromPosX || 0, 3)}</Text>
                  <Text>Starts From Y: {decimals(button?.startsFromPosY || 0, 3)}</Text>
                  <Text>
                    Columns Count: {decimals(button?.columnsCount || MIN_ROWS_COLUMNS, 3)}
                  </Text>
                  <Text>Columns Gap: {decimals(button?.columnsGap || 0, 3)}</Text>
                  <Text>Rows Count: {decimals(button?.rowsCount || MIN_ROWS_COLUMNS, 3)}</Text>
                  <Text>Rows Gap: {decimals(button?.rowsGap || 0, 3)}</Text>
                  <Text>
                    Offset Alternate Rows: {decimals(button?.offsetAlternateRows || 0, 3)}
                  </Text>
                  <Text>Diameter Size: {decimals(button?.tuftedButtonScale || MIN_SCALE, 3)}</Text>
                </View>
              </View>
            );
          })}
          {data?.tape?.map((tape, index) => {
            return (
              <View style={styles.row} key={index}>
                <Text style={styles.cell}>
                  Tape{data.tape.length > 1 && index > 0 && `-${index + 1}`}
                </Text>
                <View style={styles.multiLineCell}>
                  <Text>Variant: {tape?.name || '-'}</Text>
                  <Text>Position: {decimals(tape?.positionY || 0, 3)}</Text>
                  <Text>ScaleX: {decimals(tape?.scaleX || 1, 3)}</Text>
                  <Text>ScaleY: {decimals(tape?.scaleY || 1, 3)}</Text>
                  <Text>ScaleZ: {decimals(tape?.scaleZ || 1, 3)}</Text>
                </View>
              </View>
            );
          })}
          {data?.label?.map((label, index) => {
            const side = POSITION_DATA.find((data) => data?.value === label?.side);
            return (
              <View style={styles.row} key={index}>
                <Text style={styles.cell}>
                  Label{data.label.length > 1 && index > 0 && `-${index + 1}`}
                </Text>
                <View style={styles.multiLineCell}>
                  <Text>Variant: {label?.name || '-'}</Text>
                  <Text>Side: {side?.label}</Text>
                  <Text>PositionX: {decimals(label?.positionX || 0, 3)}</Text>
                  <Text>PositionY: {decimals(label?.positionY || 0, 3)}</Text>
                  <Text>PositionZ: {decimals(label?.positionZ || 0, 3)}</Text>
                  <Text>ScaleX: {decimals(label?.scaleX || 1, 3)}</Text>
                  <Text>ScaleY: {decimals(label?.scaleY || 1, 3)}</Text>
                  <Text>ScaleZ: {decimals(label?.scaleZ || 1, 3)}</Text>
                </View>
              </View>
            );
          })}
          {data?.handle?.map((handle, index) => {
            const side = POSITION_DATA.find((data) => data?.value === handle?.side);
            return (
              <View style={styles.row} key={index}>
                <Text style={styles.cell}>
                  Handle{data.handle.length > 1 && index > 0 && `-${index + 1}`}
                </Text>
                <View style={styles.multiLineCell}>
                  <Text>Variant: {handle?.name || '-'}</Text>
                  <Text>Side: {side?.label}</Text>
                  <Text>PositionX: {decimals(handle?.positionX || 0, 3)}</Text>
                  <Text>PositionY: {decimals(handle?.positionY || 0, 3)}</Text>
                  <Text>PositionZ: {decimals(handle?.positionZ || 0, 3)}</Text>
                  <Text>ScaleX: {decimals(handle?.scaleX || 1, 3)}</Text>
                  <Text>ScaleY: {decimals(handle?.scaleY || 1, 3)}</Text>
                  <Text>ScaleZ: {decimals(handle?.scaleZ || 1, 3)}</Text>
                </View>
              </View>
            );
          })}
          {data?.bottomData?.map((bottomData, index) => {
            return (
              <div key={index}>
                {bottomData?.bottomName && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Bottom: Variant</Text>
                    <Text style={styles.cell}>{bottomData?.bottomName}</Text>
                  </View>
                )}
                {bottomData?.patternId && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Bottom: Quilting Pattern</Text>
                    <View style={styles.multiLineCell}>
                      <Text>Name: {bottomData.patternName}</Text>
                      <Text>
                        Width ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(bottomData.width), 3)}
                      </Text>
                      <Text>
                        Height ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(bottomData.height), 3)}
                      </Text>
                      <Text>PositionX: {decimals(bottomData.positionX, 3)}</Text>
                      <Text>PositionY: {decimals(bottomData.positionY, 3)}</Text>
                      <Text>Rotation: {decimals(bottomData.rotation, 3)}</Text>
                      <Text>Depth: {decimals(bottomData.displacementIntensity, 3)}</Text>
                    </View>
                  </View>
                )}
                {bottomData?.gussetPatternId && (
                  <View style={styles.row}>
                    <Text style={styles.cell}>Bottom: Gusset</Text>
                    <View style={styles.multiLineCell}>
                      <Text>Name: {bottomData.gussetPatternName}</Text>
                      <Text>
                        Width ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(bottomData.widthGusset), 3)}
                      </Text>
                      <Text>
                        Height ({data.measureUnits}):{' '}
                        {decimals(convertCMToActiveUnits(bottomData.heightGusset), 3)}
                      </Text>
                      <Text>PositionX: {decimals(bottomData.positionXGusset, 3)}</Text>
                      <Text>PositionY: {decimals(bottomData.positionYGusset, 3)}</Text>
                      <Text>Rotation: {decimals(bottomData.rotationGusset, 3)}</Text>
                      <Text>Depth: {decimals(bottomData.displacementIntensityGusset, 3)}</Text>
                    </View>
                  </View>
                )}
              </div>
            );
          })}
          {data?.layers?.map((layer, index) => {
            return (
              <div key={index}>
                <View style={styles.row}>
                  <Text style={styles.cell}>Layer: {layer?.name}</Text>
                  <Text style={styles.cell}>
                    Height: {toUnityFixed(layer?.layerHeight, 3)} inches
                  </Text>
                </View>
              </div>
            );
          })}
          {data?.view?.view && (
            <View style={styles.row}>
              <Text style={styles.cell}>View</Text>
              <View style={styles.multiLineCell}>
                <Text>{VIEW?.[data?.view?.view]?.label}</Text>
                {data?.view?.view &&
                  data?.view?.view !== 'cutaway' &&
                  VIEW?.[data?.view?.view]?.text && (
                    <Text>
                      {VIEW?.[data?.view?.view]?.text}: {data?.view?.[`gap${data?.view?.view}`]}
                    </Text>
                  )}
                {data?.view?.view === 'cutaway' && data?.layers?.length && (
                  <Text>Shape: {data?.layers?.[0]?.cutawayShape === 0 ? 'Square' : 'Circle'}</Text>
                )}
                {data?.view?.view === 'cutaway' &&
                  data?.layers?.map((layer, index) => {
                    return (
                      <Text key={index}>
                        {layer?.name} -{' '}
                        {layer?.cutawayShape === 0 ? layer?.cutawayBoxSizeX : layer?.cutawayRadius}
                      </Text>
                    );
                  })}
              </View>
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};
